import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Select, Switch, Table, message } from "antd";
import useAuth from "../../hooks/useAuth";
import {
  ACCOUNT_LABEL,
  addPerson,
  createFirm,
  describeError,
  describeReferral,
  fetchFirm,
  fetchFirms,
  invitePerson,
  updateFirm,
  updatePerson,
} from "../../service/referralFirms";
import {
  Detail,
  Empty,
  ErrorBanner,
  Field,
  FirmItem,
  FirmList,
  FirmMeta,
  FirmName,
  Inner,
  Layout,
  Muted,
  Notice,
  Page,
  Pill,
  Row,
  Section,
  SectionHeader,
  Subtitle,
  Title,
  TitleRow,
} from "./ReferralsStyled";

/**
 * Referrals — the firm directory (Matthew, 2026-11-02: "Why am I here
 * [the raw back office] and not on the frontend to do the referral
 * stuff? … add a Referrals section now").
 *
 * Left: every outside firm we refer cases to. Right: the chosen firm —
 * its name, whether it is taking referrals, the **referral terms** the
 * firm reads on every case and must accept before taking one, the
 * practice areas and states it covers, the people there (who gets the
 * emails, who may add colleagues) with their set-up invitations, and the
 * cases we have sent it. Adding a firm here is the whole job: Litify
 * stores the firm's name on each referral, nothing to add there.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A date-only value ("2026-11-01") is a calendar day, not an instant: read
// it as local midnight so it does not show as the day before in the US.
const fmtDate = (value) => {
  if (!value) return "";
  const d = DATE_ONLY.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const STATUS_COLOURS = {
  sent: ["#dbeafe", "#1e40af"],
  signed_up: ["#dcfce7", "#166534"],
  turned_down: ["#fee2e2", "#991b1b"],
  expired: ["#fee2e2", "#991b1b"],
  withdrawn: ["#f3f4f6", "#374151"],
};

const EMPTY_FIRM = { name: "", isActive: true, feeTerms: "", practiceAreas: [], states: [], notes: "" };
const EMPTY_PERSON = { fullName: "", email: "", title: "", isAdmin: false, receivesReferrals: true, sendInvitation: true };

const toOptions = (values) => (values || []).map((value) => ({ value, label: value }));

const FirmForm = ({ value, onChange, options, nameHelp }) => (
  <>
    <Field>
      <span className="label">Firm name</span>
      <Input
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        placeholder="Berry Law"
        maxLength={255}
        data-testid="firm-name"
      />
      {nameHelp ? <span className="help">{nameHelp}</span> : null}
    </Field>
    <Field>
      <span className="label">Referral terms</span>
      <Input.TextArea
        value={value.feeTerms}
        onChange={(e) => onChange({ ...value, feeTerms: e.target.value })}
        autoSize={{ minRows: 4, maxRows: 14 }}
        placeholder="One third of the attorney fee on any recovery, paid within 30 days of receipt."
        data-testid="firm-terms"
      />
      <span className="help">
        The firm reads this on every case we send and must tick &ldquo;We accept the referral terms&rdquo; before it
        can take one. The wording is frozen onto each case as sent, so a later edit does not change earlier cases.
      </span>
    </Field>
    <Row>
      <Field>
        <span className="label">Practice areas they take</span>
        <Select
          mode="multiple"
          value={value.practiceAreas}
          onChange={(practiceAreas) => onChange({ ...value, practiceAreas })}
          options={toOptions(options.practiceAreaOptions)}
          placeholder="Choose"
          style={{ width: "100%" }}
          data-testid="firm-areas"
        />
      </Field>
      <Field>
        <span className="label">States</span>
        <Select
          mode="multiple"
          value={value.states}
          onChange={(states) => onChange({ ...value, states })}
          options={toOptions(options.stateOptions)}
          placeholder="Choose"
          style={{ width: "100%" }}
          showSearch
          data-testid="firm-states"
        />
      </Field>
    </Row>
    <Field>
      <span className="label">Notes for our team</span>
      <Input.TextArea
        value={value.notes}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
        autoSize={{ minRows: 2, maxRows: 8 }}
        placeholder="Who we deal with there, how the agreement came about — the firm never sees this."
      />
    </Field>
  </>
);

const PersonForm = ({ value, onChange, portalEnabled, showInvite = true }) => (
  <>
    <Row>
      <Field>
        <span className="label">Name</span>
        <Input
          value={value.fullName}
          onChange={(e) => onChange({ ...value, fullName: e.target.value })}
          placeholder="Sam Berry"
          data-testid="person-name"
        />
      </Field>
      <Field>
        <span className="label">Email</span>
        <Input
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
          placeholder="sam@berrylaw.com"
          type="email"
          data-testid="person-email"
        />
      </Field>
      <Field>
        <span className="label">Title</span>
        <Input
          value={value.title}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          placeholder="Partner"
        />
      </Field>
    </Row>
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 8 }}>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <Switch
          size="small"
          checked={value.receivesReferrals}
          onChange={(receivesReferrals) => onChange({ ...value, receivesReferrals })}
        />
        Gets the email when we refer a case
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <Switch size="small" checked={value.isAdmin} onChange={(isAdmin) => onChange({ ...value, isAdmin })} />
        Can add colleagues at the firm
      </label>
      {showInvite ? (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          <Switch
            size="small"
            checked={value.sendInvitation}
            onChange={(sendInvitation) => onChange({ ...value, sendInvitation })}
          />
          Email the set-up invitation now
        </label>
      ) : null}
    </div>
    {showInvite && value.sendInvitation && !portalEnabled ? (
      <Muted>The firms&rsquo; portal is not switched on here yet; the invitation will wait until it is.</Muted>
    ) : null}
  </>
);

const ReferralsPage = () => {
  const { isAdmin } = useAuth();

  const [firms, setFirms] = useState([]);
  const [meta, setMeta] = useState({ practiceAreaOptions: [], stateOptions: [], portalEnabled: false });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {firm, people, referrals}
  const [draft, setDraft] = useState(EMPTY_FIRM);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [addingFirm, setAddingFirm] = useState(false);
  const [newFirm, setNewFirm] = useState(EMPTY_FIRM);
  const [newFirmPerson, setNewFirmPerson] = useState(EMPTY_PERSON);
  const [addingPerson, setAddingPerson] = useState(false);
  const [newPerson, setNewPerson] = useState(EMPTY_PERSON);
  const [busyPerson, setBusyPerson] = useState("");
  const detailRef = useRef(null); // what the page already holds, so a fresh add is not refetched over
  const loadSeq = useRef(0); // only the latest firm load may land (fast clicking)

  const loadFirms = useCallback(async () => {
    try {
      const payload = await fetchFirms();
      setFirms(payload.firms || []);
      setMeta(payload);
      setError("");
      return payload.firms || [];
    } catch (err) {
      setError(describeError(err));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const draftFrom = (firm) => ({
    name: firm.name,
    isActive: firm.isActive,
    feeTerms: firm.feeTerms || "",
    practiceAreas: firm.practiceAreas || [],
    states: firm.states || [],
    notes: firm.notes || "",
  });

  // `keepDraft`: a person action (toggle, invite, add) answers with the whole
  // firm again; the firm's unsaved edits on screen must survive it.
  const applyDetail = useCallback((payload, { keepDraft = false } = {}) => {
    const sameFirm = detailRef.current?.firm?.id === payload.firm.id;
    detailRef.current = payload;
    setDetail(payload);
    if (!(keepDraft && sameFirm)) setDraft(draftFrom(payload.firm));
    setFirms((current) => current.map((f) => (f.id === payload.firm.id ? { ...f, ...payload.firm } : f)));
  }, []);

  const loadDetail = useCallback(
    async (firmId) => {
      if (!firmId) return;
      const seq = ++loadSeq.current;
      setLoadingDetail(true);
      try {
        const payload = await fetchFirm(firmId);
        if (seq !== loadSeq.current) return; // a later click won
        applyDetail(payload);
        setError("");
      } catch (err) {
        if (seq === loadSeq.current) setError(describeError(err));
      } finally {
        if (seq === loadSeq.current) setLoadingDetail(false);
      }
    },
    [applyDetail],
  );

  useEffect(() => {
    loadFirms().then((list) => {
      if (list.length && !selectedId) setSelectedId(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFirms]);

  useEffect(() => {
    setNote("");
    if (detailRef.current?.firm?.id === selectedId) return;
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const dirty = useMemo(() => {
    if (!detail) return false;
    const f = detail.firm;
    return (
      draft.name !== f.name ||
      draft.isActive !== f.isActive ||
      draft.feeTerms !== (f.feeTerms || "") ||
      JSON.stringify(draft.practiceAreas) !== JSON.stringify(f.practiceAreas || []) ||
      JSON.stringify(draft.states) !== JSON.stringify(f.states || []) ||
      draft.notes !== (f.notes || "")
    );
  }, [draft, detail]);

  const saveFirm = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = await updateFirm(detail.firm.id, draft);
      applyDetail(payload);
      setNote(payload.note || "");
      message.success("Saved.");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const tellAboutInvitation = (invitation) => {
    if (!invitation || invitation.invited === undefined) return;
    if (invitation.invited) message.success(invitation.message);
    else message.warning(invitation.message);
  };

  const submitNewFirm = async () => {
    setSaving(true);
    try {
      const body = { ...newFirm };
      if (newFirmPerson.email || newFirmPerson.fullName) body.firstPerson = newFirmPerson;
      const payload = await createFirm(body);
      setAddingFirm(false);
      setNewFirm(EMPTY_FIRM);
      setNewFirmPerson(EMPTY_PERSON);
      await loadFirms();
      loadSeq.current += 1;
      applyDetail(payload);
      setSelectedId(payload.firm.id);
      message.success(`${payload.firm.name} added.`);
      tellAboutInvitation(payload.invitation);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitNewPerson = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = await addPerson(detail.firm.id, newPerson);
      setAddingPerson(false);
      setNewPerson(EMPTY_PERSON);
      applyDetail(payload, { keepDraft: true });
      message.success(`${newPerson.fullName} added.`);
      tellAboutInvitation(payload.invitation);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePerson = async (person, body) => {
    setBusyPerson(person.id);
    try {
      applyDetail(await updatePerson(person.id, body), { keepDraft: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyPerson("");
    }
  };

  const sendInvitation = async (person) => {
    setBusyPerson(person.id);
    try {
      const payload = await invitePerson(person.id);
      applyDetail(payload, { keepDraft: true });
      tellAboutInvitation(payload.invitation);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyPerson("");
    }
  };

  if (isAdmin === false) {
    return (
      <Page>
        <Title>Referrals</Title>
        <Empty>This section is for administrators.</Empty>
      </Page>
    );
  }

  const peopleColumns = [
    {
      title: "Name",
      dataIndex: "fullName",
      render: (value, person) => (
        <div>
          <div style={{ fontWeight: 500, color: person.isActive ? "#111827" : "#9ca3af" }}>{value}</div>
          <Muted>
            {person.email}
            {person.title ? ` · ${person.title}` : ""}
          </Muted>
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "account",
      render: (value, person) => (
        <div>
          <div>{ACCOUNT_LABEL[value] || value}</div>
          {person.lastSignInAt ? <Muted>Last signed in {fmtDate(person.lastSignInAt)}</Muted> : null}
        </div>
      ),
    },
    {
      title: "Gets the emails",
      dataIndex: "receivesReferrals",
      align: "center",
      render: (value, person) => (
        <Switch
          size="small"
          checked={value}
          disabled={!person.isActive || busyPerson === person.id}
          onChange={(checked) => changePerson(person, { receivesReferrals: checked })}
          aria-label={`${person.fullName} gets the emails`}
        />
      ),
    },
    {
      title: "Can add colleagues",
      dataIndex: "isAdmin",
      align: "center",
      render: (value, person) => (
        <Switch
          size="small"
          checked={value}
          disabled={!person.isActive || busyPerson === person.id}
          onChange={(checked) => changePerson(person, { isAdmin: checked })}
          aria-label={`${person.fullName} can add colleagues`}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, person) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {person.isActive && person.account !== "ready" ? (
            <Button size="small" loading={busyPerson === person.id} onClick={() => sendInvitation(person)}>
              {person.account === "not_invited" ? "Send invitation" : "Send again"}
            </Button>
          ) : null}
          {person.isActive ? (
            <Button
              size="small"
              danger
              loading={busyPerson === person.id}
              onClick={() =>
                Modal.confirm({
                  title: `Deactivate ${person.fullName}?`,
                  content: "They are signed out everywhere and can no longer open the portal. You can reactivate them later.",
                  okText: "Deactivate",
                  okButtonProps: { danger: true },
                  onOk: () => changePerson(person, { isActive: false }),
                })
              }
            >
              Deactivate
            </Button>
          ) : (
            <Button size="small" loading={busyPerson === person.id} onClick={() => changePerson(person, { isActive: true })}>
              Reactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  const referralColumns = [
    {
      title: "Client",
      dataIndex: "client",
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{value || "—"}</div>
          <Muted>
            {[row.caseType || row.practiceArea, row.state, row.intakeNumber].filter(Boolean).join(" · ")}
          </Muted>
        </div>
      ),
    },
    {
      title: "Where it stands",
      key: "where",
      render: (_, row) => {
        const [bg, fg] = STATUS_COLOURS[row.status] || STATUS_COLOURS.withdrawn;
        return (
          <Pill $bg={bg} $fg={fg}>
            {describeReferral(row)}
          </Pill>
        );
      },
    },
    { title: "Sent", dataIndex: "sentAt", render: fmtDate },
    {
      title: "Answered",
      dataIndex: "decidedAt",
      render: (value, row) => (value ? fmtDate(value) : row.status === "sent" ? `Expires ${fmtDate(row.expiresAt)}` : ""),
    },
    { title: "Signed on", dataIndex: "signedUpDate", render: fmtDate },
    { title: "Papers", dataIndex: "documentsReceived", align: "right", render: (n) => (n ? n : "") },
  ];

  return (
    <Page>
      <Inner>
      <TitleRow>
        <div>
          <Title>Referrals</Title>
          <Subtitle>
            The firms we refer cases to: their terms, their people, and every case we have sent them.
            {meta.portalEnabled ? "" : " The firms' portal is not switched on here yet; invitations wait until it is."}
          </Subtitle>
        </div>
        <Button type="primary" onClick={() => setAddingFirm(true)} data-testid="add-firm">
          Add a firm
        </Button>
      </TitleRow>

      {error ? <ErrorBanner role="alert">{error}</ErrorBanner> : null}

      <Layout>
        <FirmList aria-label="Firms">
          {loading ? (
            <Empty>Loading…</Empty>
          ) : firms.length === 0 ? (
            <Empty>No firms yet. Add the first one to start referring cases.</Empty>
          ) : (
            firms.map((firm) => (
              <FirmItem
                key={firm.id}
                type="button"
                $selected={firm.id === selectedId}
                onClick={() => setSelectedId(firm.id)}
                data-testid={`firm-${firm.id}`}
              >
                <FirmName $inactive={!firm.isActive}>
                  {firm.name}
                  {firm.isActive ? "" : " (not taking referrals)"}
                </FirmName>
                <FirmMeta>
                  {firm.waiting ? `${firm.waiting} waiting · ` : ""}
                  {firm.total || 0} referred · {firm.people || 0} {firm.people === 1 ? "person" : "people"}
                </FirmMeta>
              </FirmItem>
            ))
          )}
        </FirmList>

        <Detail>
          {!detail ? (
            <Section>
              <Empty>{loadingDetail ? "Loading…" : "Choose a firm, or add one."}</Empty>
            </Section>
          ) : (
            <>
              <Section>
                <SectionHeader>
                  <h2>{detail.firm.name}</h2>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                    <Switch
                      checked={draft.isActive}
                      onChange={(isActive) => setDraft({ ...draft, isActive })}
                      data-testid="firm-active"
                    />
                    Taking referrals
                  </label>
                </SectionHeader>
                {note ? <Notice>{note}</Notice> : null}
                <FirmForm
                  value={draft}
                  onChange={setDraft}
                  options={meta}
                  nameHelp={
                    detail.firm.litifyName && detail.firm.litifyName !== detail.firm.name
                      ? `Litify knows this firm as “${detail.firm.litifyName}”.`
                      : "This is also the name Litify stores on each referral."
                  }
                />
                <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                  <Button onClick={() => setDraft(draftFrom(detail.firm))} disabled={!dirty || saving}>
                    Discard changes
                  </Button>
                  <Button type="primary" onClick={saveFirm} disabled={!dirty} loading={saving} data-testid="save-firm">
                    Save
                  </Button>
                </div>
              </Section>

              <Section>
                <SectionHeader>
                  <h2>People at {detail.firm.name}</h2>
                  <Button onClick={() => setAddingPerson(true)} data-testid="add-person">
                    Add a person
                  </Button>
                </SectionHeader>
                {detail.people.length === 0 ? (
                  <Empty>Nobody yet. Add the person who should receive our referrals.</Empty>
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    columns={peopleColumns}
                    dataSource={detail.people}
                  />
                )}
              </Section>

              <Section>
                <SectionHeader>
                  <h2>Cases referred to {detail.firm.name}</h2>
                  <Muted>{detail.referrals.length === 0 ? "" : `${detail.referrals.length} shown`}</Muted>
                </SectionHeader>
                {detail.referrals.length === 0 ? (
                  <Empty>No cases referred yet. Refer one from the intake review page.</Empty>
                ) : (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={detail.referrals.length > 25 ? { pageSize: 25 } : false}
                    columns={referralColumns}
                    dataSource={detail.referrals}
                  />
                )}
              </Section>
            </>
          )}
        </Detail>
      </Layout>
      </Inner>

      <Modal
        title="Add a firm"
        open={addingFirm}
        onCancel={() => setAddingFirm(false)}
        onOk={submitNewFirm}
        okText="Add firm"
        okButtonProps={{ disabled: !newFirm.name.trim(), loading: saving, "data-testid": "confirm-add-firm" }}
        width={720}
        destroyOnHidden
      >
        <FirmForm value={newFirm} onChange={setNewFirm} options={meta} nameHelp="Exactly as the firm should see it; Litify stores this name on each referral." />
        <SectionHeader>
          <h2>First person at the firm</h2>
          <Muted>Optional — you can add people later.</Muted>
        </SectionHeader>
        <PersonForm
          value={{ ...newFirmPerson, isAdmin: true }}
          onChange={(v) => setNewFirmPerson({ ...v, isAdmin: true })}
          portalEnabled={meta.portalEnabled}
        />
      </Modal>

      <Modal
        title={detail ? `Add a person at ${detail.firm.name}` : "Add a person"}
        open={addingPerson}
        onCancel={() => setAddingPerson(false)}
        onOk={submitNewPerson}
        okText="Add person"
        okButtonProps={{
          disabled: !newPerson.fullName.trim() || !newPerson.email.trim(),
          loading: saving,
          "data-testid": "confirm-add-person",
        }}
        width={720}
        destroyOnHidden
      >
        <PersonForm value={newPerson} onChange={setNewPerson} portalEnabled={meta.portalEnabled} />
      </Modal>
    </Page>
  );
};

export default ReferralsPage;
